/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import FlagIcon from '@material-ui/icons/Flag';

// Suggestion categories
const suggestionCategories = [
  { value: 'development', name: 'Development', nameAr: 'تطوير' },
  { value: 'improvement', name: 'Improvement', nameAr: 'تحسين' },
  { value: 'complaint', name: 'Complaint', nameAr: 'شكوى' },
  { value: 'other', name: 'Other', nameAr: 'أخرى' },
];

const PopupSuggestionView = ({
  open,
  onClose,
  row,
  theme,
  company,
  isRTL,
  words,
}: any) => {
  if (!row) return null;

  const formatDate = (date: any) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US');
  };

  const getCategoryName = (value: string) => {
    const cat = suggestionCategories.find((c) => c.value === value);
    return isRTL ? cat?.nameAr : cat?.name;
  };

  const priorityColors: any = {
    low: '#4caf50',
    medium: '#ff9800',
    high: '#f44336',
    urgent: '#9c27b0',
  };

  const priorityLabels: any = {
    low: isRTL ? 'منخفض' : 'Low',
    medium: isRTL ? 'متوسط' : 'Medium',
    high: isRTL ? 'عالي' : 'High',
    urgent: isRTL ? 'عاجل' : 'Urgent',
  };

  const statusColors: any = {
    new: '#2196f3',
    reviewing: '#ff9800',
    approved: '#4caf50',
    rejected: '#f44336',
    implemented: '#9c27b0',
  };

  const statusLabels: any = {
    new: isRTL ? 'جديد' : 'New',
    reviewing: isRTL ? 'قيد المراجعة' : 'Reviewing',
    approved: isRTL ? 'مقبول' : 'Approved',
    rejected: isRTL ? 'مرفوض' : 'Rejected',
    implemented: isRTL ? 'تم التنفيذ' : 'Implemented',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
        },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            {isRTL ? 'تفاصيل الاقتراح' : 'Suggestion Details'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent style={{ padding: 24 }}>
        <Grid container spacing={3}>
          {/* Title Section */}
          <Grid item xs={12}>
            <Box
              style={{
                backgroundColor: '#f5f5f5',
                borderRadius: 8,
                padding: 16,
              }}
            >
              <Typography
                variant="h5"
                style={{ fontWeight: 'bold', marginBottom: 8 }}
              >
                {row.title}
              </Typography>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                <Chip
                  label={getCategoryName(row.category)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<FlagIcon />}
                  label={priorityLabels[row.priority] || row.priority}
                  size="small"
                  style={{
                    backgroundColor: priorityColors[row.priority] || '#9e9e9e',
                    color: '#fff',
                  }}
                />
                <Chip
                  label={statusLabels[row.status] || row.status}
                  size="small"
                  style={{
                    backgroundColor: statusColors[row.status] || '#9e9e9e',
                    color: '#fff',
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Submitted By */}
          <Grid item xs={6}>
            <Box display="flex" alignItems="center">
              <PersonIcon
                style={{ marginRight: 8, color: '#1976d2', fontSize: 24 }}
              />
              <Box>
                <Typography color="textSecondary" variant="caption">
                  {isRTL ? 'مقدم الاقتراح' : 'Submitted By'}
                </Typography>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {isRTL ? row.employeeNameAr : row.employeeName || '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography color="textSecondary" variant="caption">
              {isRTL ? 'تاريخ الإرسال' : 'Submitted On'}
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {formatDate(row.createdAt)}
            </Typography>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <Box
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                padding: 16,
              }}
            >
              <Typography
                color="textSecondary"
                variant="caption"
                style={{ marginBottom: 8, display: 'block' }}
              >
                {isRTL ? 'تفاصيل الاقتراح' : 'Suggestion Details'}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                {row.desc || (isRTL ? 'لا توجد تفاصيل' : 'No details provided')}
              </Typography>
            </Box>
          </Grid>

          {/* Response Section */}
          {row.response && (
            <Grid item xs={12}>
              <Box
                style={{
                  border: '2px solid #4caf50',
                  borderRadius: 8,
                  padding: 16,
                  backgroundColor: '#e8f5e9',
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{ color: '#2e7d32', marginBottom: 8 }}
                >
                  {isRTL ? 'رد الإدارة' : 'Management Response'}
                </Typography>
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {row.response}
                </Typography>
                {row.responseDate && (
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    style={{ marginTop: 8, display: 'block' }}
                  >
                    {isRTL ? 'تاريخ الرد:' : 'Response Date:'}{' '}
                    {formatDate(row.responseDate)}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions style={{ padding: 16 }}>
        <Button onClick={onClose} variant="outlined">
          {isRTL ? 'إغلاق' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PopupSuggestionView;
